import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  CheckIcon,
  Checkbox,
  ConfirmDialog,
  CopyIcon,
  DangerButton,
  FormField,
  IconButton,
  Input,
  Modal,
  PrimaryButton,
  SearchInput,
  SecondaryButton,
  XIcon,
  debounce,
} from "../../design-system";
import { borders, interactive, surfaces, textColors, typography } from "../../design-system/tokens";
import CouponPackPricingEditor from "./CouponPackPricingEditor";
import {
  useAdminContainerVersions,
  useAffiliateUserCoupons,
  useAffiliateUsers,
  useRegenerateAffiliatePassword,
  useSendAffiliateWelcomeEmail,
  useSetAffiliateFlag,
  useSetCommercialKind,
} from "../hooks/useAdminAffiliates";
import { useApplyUserCouponDefaults, useCouponDefault } from "../hooks/useAdminCouponDefaults";
import {
  useDeleteUserCouponPackPricing,
  useSaveUserCouponPackPricing,
  useUserCouponPackPricing,
} from "../hooks/useAdminCouponPackPricing";
import { useUserDetail } from "../hooks/useAdminStats";
import { setAffiliatePromotedCoupon } from "../utils/adminApi";
import { buildDefaultCouponCode, buildAlternativeDefaultCouponCode, DEFAULT_ACCESS_MAX_COUNT } from "../utils/couponCodeUtils";
import {
  COUPON_STATUS_BADGE_CLASSES,
  couponStatusLabel,
  sortCouponsByStatus,
} from "../../affiliate/utils/couponStatus";

const DEFAULT_LIMIT = 50;

const isSystemTemplatePreviewUser = (u) => {
  const username = String(u?.username || "").toLowerCase();
  const email = String(u?.email || "").toLowerCase();
  return username === "template-preview-system" || email === "template-preview-system@myvitae.ai";
};

// Accounts created without an email get a generated address that cannot receive mail.
const PLACEHOLDER_EMAIL_DOMAIN = "placeholder.myvitae.ai";
const hasRealEmail = (email) => {
  const value = String(email || "").trim().toLowerCase();
  return !!value && !value.endsWith(`@${PLACEHOLDER_EMAIL_DOMAIN}`);
};

const formatIsoDateTime = (isoString) => {
  if (!isoString) return "—";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return isoString;
  return d.toLocaleString();
};

// The three kinds an account can have. `commercial_kind` only distinguishes the
// two portal kinds — it keeps its "affiliate" default on plain accounts — so the
// portal flag decides first and a plain user is never labelled "Affiliate".
const ACCOUNT_KIND_USER = "user";
const ACCOUNT_KIND_AFFILIATE = "affiliate";
const ACCOUNT_KIND_PARTNER = "partner";
const ACCOUNT_KINDS = [ACCOUNT_KIND_USER, ACCOUNT_KIND_AFFILIATE, ACCOUNT_KIND_PARTNER];

const getAccountKind = (userRecord) => {
  const serverKind = userRecord?.account_kind;
  if (ACCOUNT_KINDS.includes(serverKind)) return serverKind;
  // Older payloads carry no account_kind; rebuild it from the portal flag.
  if (!userRecord?.is_influencer) return ACCOUNT_KIND_USER;
  return userRecord?.commercial_kind === ACCOUNT_KIND_PARTNER ? ACCOUNT_KIND_PARTNER : ACCOUNT_KIND_AFFILIATE;
};

const isPortalKind = (kind) => kind === ACCOUNT_KIND_AFFILIATE || kind === ACCOUNT_KIND_PARTNER;

const ACCOUNT_KIND_LABELS = {
  [ACCOUNT_KIND_USER]: "User",
  [ACCOUNT_KIND_AFFILIATE]: "Affiliate",
  [ACCOUNT_KIND_PARTNER]: "Partner",
};

const ACCOUNT_KIND_ARTICLE_LABELS = {
  [ACCOUNT_KIND_USER]: "a plain user",
  [ACCOUNT_KIND_AFFILIATE]: "an affiliate",
  [ACCOUNT_KIND_PARTNER]: "a partner",
};

// What each kind means, shown on the confirm dialog before the switch is made.
const ACCOUNT_KIND_CONSEQUENCES = {
  [ACCOUNT_KIND_USER]:
    "loses the portal role entirely: no Affiliate Portal access and no coupon management. Existing coupon links and referral history stay in place.",
  [ACCOUNT_KIND_AFFILIATE]:
    "becomes an affiliate: they can use the Affiliate Portal, earn commission on referred sales, and get the My Payments tab.",
  [ACCOUNT_KIND_PARTNER]:
    "becomes a partner: they can use the portal to buy or receive coupon packs, and lose the My Payments tab.",
};

const ACCOUNT_KIND_BADGE_CLASSES = {
  [ACCOUNT_KIND_PARTNER]: "bg-blue-100 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300",
  [ACCOUNT_KIND_AFFILIATE]:
    "bg-violet-100 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-800 text-violet-800 dark:text-violet-300",
};

const kindLabel = (kind) => ACCOUNT_KIND_LABELS[kind] || ACCOUNT_KIND_LABELS[ACCOUNT_KIND_AFFILIATE];
const kindArticleLabel = (kind) =>
  ACCOUNT_KIND_ARTICLE_LABELS[kind] || ACCOUNT_KIND_ARTICLE_LABELS[ACCOUNT_KIND_AFFILIATE];

/** The coloured pill next to a username. */
const AccountKindBadge = ({ kind }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full ${typography.caption} ${
      ACCOUNT_KIND_BADGE_CLASSES[kind] || `${surfaces.glass} ${borders.glass} border ${textColors.secondary}`
    }`}
  >
    {kindLabel(kind)}
  </span>
);

// Account kinds the list can be filtered by. Any combination can be checked;
// none checked means "all kinds".
const ACCOUNT_KIND_OPTIONS = [
  { value: ACCOUNT_KIND_USER, label: "Users" },
  { value: ACCOUNT_KIND_AFFILIATE, label: "Affiliates" },
  { value: ACCOUNT_KIND_PARTNER, label: "Partners" },
];

// Cap how many created codes are spelled out in the success notice.
const NOTICE_CODE_PREVIEW_COUNT = 5;

/** Status pill, coloured by the shared affiliate-portal palette. */
const CouponStatusPill = ({ status }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full ${typography.caption} ${
      COUPON_STATUS_BADGE_CLASSES[status] || `${surfaces.glass} ${borders.glass} border ${textColors.secondary}`
    }`}
  >
    {couponStatusLabel(status)}
  </span>
);

/** "1/1", "0/2" — or the bare redeemed count when the coupon has no cap. */
const formatCouponUsage = (coupon) => {
  const used = coupon.times_redeemed ?? 0;
  const max = coupon.max_redemptions;
  return max == null ? String(used) : `${used}/${max}`;
};

/**
 * Who an access coupon was emailed to. One recipient reads as the address itself;
 * several collapse to "N people" and expand on click, so the column stays narrow.
 */
const SentRecipientsCell = ({ coupon, isOpen, onToggle }) => {
  const recipients = Array.isArray(coupon.sent_recipients) ? coupon.sent_recipients : [];
  if (recipients.length === 0) {
    return <span className={`${typography.bodySmall} ${textColors.muted}`}>—</span>;
  }
  if (recipients.length === 1) {
    return <span className={`${typography.bodySmall} ${textColors.secondary} break-all`}>{recipients[0]}</span>;
  }
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-label={`Show the ${recipients.length} people ${coupon.code} was sent to`}
        className={`inline-flex items-center gap-1 ${typography.bodySmall} ${textColors.secondary} underline underline-offset-2`}
      >
        {recipients.length} people
      </button>
      {isOpen && (
        <ul
          className={`mt-1 rounded-xl border ${borders.glass} ${surfaces.glassCard} p-2 space-y-1`}
          aria-label={`People ${coupon.code} was sent to`}
        >
          {recipients.map((email) => (
            <li key={email} className={`${typography.caption} ${textColors.secondary} break-all`}>
              {email}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * One table of coupons linked to an affiliate or partner.
 *
 * Regular and access coupons answer different questions, so the columns differ:
 * a regular coupon is promoted or not, while an access coupon is tracked by how
 * far through its usages it is and who it was emailed to.
 */
const LinkedCouponsTable = ({
  title,
  emptyMessage,
  coupons,
  isAccess = false,
  promotedCouponId = null,
  promoteLoading = false,
  onPromote = null,
  openRecipientsCouponId = null,
  onToggleRecipients = null,
}) => (
  <div>
    <div className={`mb-2 ${typography.label} ${textColors.primary}`}>{title}</div>
    {coupons.length === 0 ? (
      <div className={`${typography.bodySmall} ${textColors.secondary}`}>{emptyMessage}</div>
    ) : (
      <div className={`border rounded-2xl overflow-hidden ${borders.glass}`}>
        <div className={`overflow-x-auto ${surfaces.glassCard}`}>
          <table className="min-w-full text-left" aria-label={`${title} linked to this account`}>
            <thead className={surfaces.glass}>
              <tr className={`${textColors.secondary} ${typography.caption}`}>
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">Status</th>
                {isAccess && <th className="px-3 py-2">Used</th>}
                {isAccess && <th className="px-3 py-2">Sent to</th>}
                <th className="px-3 py-2">Redeem by</th>
                <th className="px-3 py-2">Active</th>
                {!isAccess && <th className="px-3 py-2">Promoted</th>}
              </tr>
            </thead>
            <tbody className={surfaces.glassCard}>
              {coupons.map((c) => (
                <tr key={c.id} className={`${borders.glass} border-t`}>
                  <td className={`px-3 py-2 ${typography.bodySmall} ${textColors.primary}`}>{c.code}</td>
                  <td className="px-3 py-2">
                    <CouponStatusPill status={c.status} />
                  </td>
                  {isAccess && (
                    <td className={`px-3 py-2 ${typography.bodySmall} ${textColors.secondary}`}>
                      {formatCouponUsage(c)}
                    </td>
                  )}
                  {isAccess && (
                    <td className="px-3 py-2">
                      <SentRecipientsCell
                        coupon={c}
                        isOpen={String(openRecipientsCouponId) === String(c.id)}
                        onToggle={() => onToggleRecipients?.(c.id)}
                      />
                    </td>
                  )}
                  <td className={`px-3 py-2 ${typography.bodySmall} ${textColors.secondary}`}>
                    {formatIsoDateTime(c.redeem_by)}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full ${typography.caption} ${
                        c.is_effectively_active
                          ? "bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300"
                          : `${surfaces.glass} ${borders.glass} border ${textColors.secondary}`
                      }`}
                    >
                      {c.is_effectively_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  {!isAccess && (
                    <td className="px-3 py-2">
                      {String(c.id) === String(promotedCouponId) ? (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full ${typography.caption} bg-blue-100 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300`}
                        >
                          Promoted
                        </span>
                      ) : (
                        <SecondaryButton
                          type="button"
                          size="sm"
                          disabled={promoteLoading || !c.is_effectively_active}
                          onClick={() => onPromote?.(c.id)}
                        >
                          Promote
                        </SecondaryButton>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
  </div>
);

const EMPTY_DEFAULTS_FORM = {
  type: null, // null | "regular" | "access"
  code: "",
  codeTouched: false,
  count: "",
  countTouched: false,
};

/**
 * Shared admin users/affiliates table (search, toggle, expand websites/coupons).
 *
 * @param {object} props
 * @param {boolean} [props.forceOnlyAffiliates=false] When true, always lists
 *   portal users only, so the plain "Users" kind is not offered as a filter.
 */
const AdminAffiliateUsersPanel = ({ forceOnlyAffiliates = false } = {}) => {
  const navigate = useNavigate();
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  // Checked account kinds; empty means no kind filter (all kinds).
  const [selectedKinds, setSelectedKinds] = useState([]); // ("user"|"affiliate"|"partner")[]
  const [notice, setNotice] = useState(null); // { type, message }
  // The kind picker: `nextKind` is null until the admin chooses one in the modal.
  const [kindChangeTarget, setKindChangeTarget] = useState(null); // { user, nextKind } | null
  const [pricingUser, setPricingUser] = useState(null); // user | null (pack pricing modal)
  const [pricingNotice, setPricingNotice] = useState(null); // { type, message }
  const [isRemoveOverrideConfirmOpen, setIsRemoveOverrideConfirmOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null); // {id, username, ...}
  const [expandedContainerId, setExpandedContainerId] = useState(null); // container whose versions are shown
  const [promoteNotice, setPromoteNotice] = useState(null); // { type, message }
  const [promoteLoading, setPromoteLoading] = useState(false);
  const [affiliateAddedUser, setAffiliateAddedUser] = useState(null); // { id, username, email, kind, must_complete_onboarding? }
  const [welcomeEmailTargetUser, setWelcomeEmailTargetUser] = useState(null); // affiliate row
  const [isWelcomeEmailDialogOpen, setIsWelcomeEmailDialogOpen] = useState(false);
  const [welcomeEmailSentUserId, setWelcomeEmailSentUserId] = useState(null); // number|null
  const [welcomeEmailError, setWelcomeEmailError] = useState(null); // string|null

  const [passwordModal, setPasswordModal] = useState(null); // { username, password, welcomeEmailSent? } | null
  const [passwordCopyNotice, setPasswordCopyNotice] = useState(null); // { type, message }
  const [regenerateTarget, setRegenerateTarget] = useState(null); // user | null
  // Which access coupon's recipient list is expanded (only one at a time).
  const [openRecipientsCouponId, setOpenRecipientsCouponId] = useState(null);
  // "Add default coupon(s)" — rendered inline inside the expanded affiliate row,
  // so it always targets whichever user is currently expanded (`selectedUser`).
  const [defaultsForm, setDefaultsForm] = useState(EMPTY_DEFAULTS_FORM);
  const [defaultsErrors, setDefaultsErrors] = useState({}); // { code?, count?, form? }
  // Confirm before granting or blocking Affiliate Portal access (harder to undo by accident).
  const [affiliateToggleConfirm, setAffiliateToggleConfirm] = useState(null); // { user, nextValue } | null

  const debouncedSetQ = useMemo(() => debounce((value) => setQ(value), 300), []);

  const hasAffiliatePortalAccess = (userRecord) => {
    if (!userRecord) return false;
    if (typeof userRecord.affiliate_portal_access === "boolean") {
      return userRecord.affiliate_portal_access;
    }
    return !!userRecord.is_influencer && !userRecord.affiliate_portal_blocked;
  };

  const kindOptions = forceOnlyAffiliates
    ? ACCOUNT_KIND_OPTIONS.filter((option) => option.value !== "user")
    : ACCOUNT_KIND_OPTIONS;
  const toggleKind = (value) =>
    setSelectedKinds((prev) => (prev.includes(value) ? prev.filter((k) => k !== value) : [...prev, value]));

  const usersQuery = useAffiliateUsers({
    q,
    only_affiliates: forceOnlyAffiliates,
    kinds: selectedKinds,
    limit: DEFAULT_LIMIT,
  });
  const setFlagMutation = useSetAffiliateFlag();
  const setCommercialKindMutation = useSetCommercialKind();
  const regeneratePasswordMutation = useRegenerateAffiliatePassword();
  const sendWelcomeMutation = useSendAffiliateWelcomeEmail();
  const userPricingQuery = useUserCouponPackPricing(pricingUser?.id, { enabled: !!pricingUser?.id });
  const saveUserPricingMutation = useSaveUserCouponPackPricing();
  const deleteUserPricingMutation = useDeleteUserCouponPackPricing();
  const couponsQuery = useAffiliateUserCoupons(selectedUser?.id, { enabled: !!selectedUser?.id });
  const promotedCouponId = couponsQuery.data?.promoted_coupon_id ?? null;
  const linkedCoupons = useMemo(() => couponsQuery.data?.coupons || [], [couponsQuery.data]);
  const regularLinkedCoupons = useMemo(
    () => linkedCoupons.filter((c) => !c.is_access_coupon),
    [linkedCoupons],
  );
  // Unused first, fully used last — the same ordering the portal's own table uses.
  const accessLinkedCoupons = useMemo(
    () => sortCouponsByStatus(linkedCoupons.filter((c) => c.is_access_coupon)),
    [linkedCoupons],
  );
  const userDetailQuery = useUserDetail(selectedUser?.id, !!selectedUser?.id);
  const userContainers = userDetailQuery.data?.containers || [];
  const containerVersionsQuery = useAdminContainerVersions(expandedContainerId, {
    enabled: !!expandedContainerId,
  });
  const regularDefaultQuery = useCouponDefault("regular", { enabled: !!selectedUser?.id });
  const accessDefaultQuery = useCouponDefault("access", { enabled: !!selectedUser?.id });
  const applyDefaultsMutation = useApplyUserCouponDefaults();
  const regularDefaultConfigured = Boolean(regularDefaultQuery.data?.configured);
  const accessDefaultConfigured = Boolean(accessDefaultQuery.data?.configured);
  const regularDefaultSuffix = regularDefaultQuery.data?.template?.suffix || "";

  // Collapse any open versions panel whenever the selected user changes, and clear the
  // inline defaults form so one affiliate's edits never carry over to the next.
  useEffect(() => {
    setExpandedContainerId(null);
    setOpenRecipientsCouponId(null);
    setDefaultsForm(EMPTY_DEFAULTS_FORM);
    setDefaultsErrors({});
  }, [selectedUser?.id]);

  // Prefill the inline defaults form from the templates (username + suffix, default
  // count) until the admin edits the fields manually.
  useEffect(() => {
    if (!selectedUser) return;
    const data = regularDefaultQuery.data;
    if (!data?.configured) return;
    setDefaultsForm((f) =>
      f.codeTouched ? f : { ...f, code: buildDefaultCouponCode(selectedUser.username, data.template?.suffix) },
    );
  }, [selectedUser, regularDefaultQuery.data]);

  useEffect(() => {
    if (!selectedUser) return;
    const data = accessDefaultQuery.data;
    if (!data?.configured) return;
    setDefaultsForm((f) =>
      f.countTouched ? f : { ...f, count: String(data.template?.default_count ?? 1) },
    );
  }, [selectedUser, accessDefaultQuery.data]);

  const users = (usersQuery.data?.results || []).filter((u) => !isSystemTemplatePreviewUser(u));

  const requestAffiliateToggle = (userRecord, nextValue) => {
    setNotice(null);
    setAffiliateToggleConfirm({ user: userRecord, nextValue: !!nextValue });
  };

  const handleConfirmAffiliateToggle = async () => {
    const pending = affiliateToggleConfirm;
    if (!pending?.user?.id) return;
    const { user: userRecord, nextValue } = pending;
    setNotice(null);
    setWelcomeEmailError(null);
    try {
      await setFlagMutation.mutateAsync({ userId: userRecord.id, is_affiliate: nextValue });

      if (nextValue) {
        setAffiliateAddedUser({
          id: userRecord.id,
          username: userRecord.username,
          email: userRecord.email,
          kind: getAccountKind(userRecord),
          must_complete_onboarding: !!userRecord.must_complete_onboarding,
        });
        setWelcomeEmailSentUserId(null);
        setNotice({
          type: "success",
          message: `${userRecord.username} can access the Affiliate Portal.`,
        });
        return;
      }

      // Blocking keeps the affiliate role; only portal access is restricted.
      setAffiliateAddedUser((prev) => (String(prev?.id) === String(userRecord.id) ? null : prev));
      setIsWelcomeEmailDialogOpen(false);
      setWelcomeEmailTargetUser((prev) => (String(prev?.id) === String(userRecord.id) ? null : prev));
      setWelcomeEmailSentUserId((prev) => (String(prev) === String(userRecord.id) ? null : prev));
      setNotice({
        type: "success",
        message: `${userRecord.username} remains an affiliate but is blocked from the Affiliate Portal.`,
      });
    } catch (err) {
      setNotice({ type: "error", message: err?.message || "Failed to update affiliate portal access." });
    } finally {
      setAffiliateToggleConfirm(null);
    }
  };

  const openKindPicker = (userRecord) => {
    setNotice(null);
    setKindChangeTarget({ user: userRecord, nextKind: null });
  };

  const closeKindPicker = () => {
    if (setCommercialKindMutation.isPending) return;
    setKindChangeTarget(null);
  };

  const handleConfirmKindChange = async () => {
    const pending = kindChangeTarget;
    if (!pending?.user?.id || !pending.nextKind) return;
    const { user: userRecord, nextKind } = pending;
    const wasPortalAccount = isPortalKind(getAccountKind(userRecord));
    setNotice(null);
    setWelcomeEmailError(null);
    try {
      await setCommercialKindMutation.mutateAsync({ userId: userRecord.id, commercial_kind: nextKind });
      // The open row reads its kind from the refreshed list row, so keeping the
      // selection is safe — but a demoted account has no coupon sections left.
      if (!wasPortalAccount && isPortalKind(nextKind)) {
        // Granting portal access is the moment to offer the welcome email, the
        // same way the portal-access toggle does.
        setAffiliateAddedUser({
          id: userRecord.id,
          username: userRecord.username,
          email: userRecord.email,
          kind: nextKind,
          must_complete_onboarding: !!userRecord.must_complete_onboarding,
        });
        setWelcomeEmailSentUserId(null);
      } else if (nextKind === ACCOUNT_KIND_USER) {
        setAffiliateAddedUser((prev) => (String(prev?.id) === String(userRecord.id) ? null : prev));
      }
      setNotice({ type: "success", message: `${userRecord.username} is now ${kindArticleLabel(nextKind)}.` });
    } catch (err) {
      setNotice({ type: "error", message: err?.message || "Failed to change account kind." });
    } finally {
      setKindChangeTarget(null);
    }
  };

  const closePricingModal = () => {
    setPricingUser(null);
    setPricingNotice(null);
    setIsRemoveOverrideConfirmOpen(false);
  };

  const handleSaveUserPricing = async (tiers) => {
    if (!pricingUser?.id) return;
    setPricingNotice(null);
    try {
      await saveUserPricingMutation.mutateAsync({ userId: pricingUser.id, tiers });
      setPricingNotice({ type: "success", message: "Pricing override saved." });
    } catch (err) {
      setPricingNotice({ type: "error", message: err?.message || "Failed to save pricing override." });
    }
  };

  const handleRemoveUserPricing = async () => {
    if (!pricingUser?.id) return;
    setPricingNotice(null);
    try {
      await deleteUserPricingMutation.mutateAsync({ userId: pricingUser.id });
      setPricingNotice({
        type: "success",
        message: "Pricing override removed. This user now uses the default table.",
      });
    } catch (err) {
      setPricingNotice({ type: "error", message: err?.message || "Failed to remove pricing override." });
    } finally {
      setIsRemoveOverrideConfirmOpen(false);
    }
  };

  const handleRegeneratePassword = async () => {
    const target = regenerateTarget;
    if (!target?.id) return;
    setNotice(null);
    try {
      const result = await regeneratePasswordMutation.mutateAsync(target.id);
      setPasswordCopyNotice(null);
      setPasswordModal({
        username: target.username,
        password: result?.initial_password || "",
        mode: "regenerated",
      });
    } catch (err) {
      setNotice({ type: "error", message: err?.message || "Failed to regenerate password." });
    } finally {
      setRegenerateTarget(null);
    }
  };

  const handleSendWelcomeEmail = async () => {
    const target = welcomeEmailTargetUser;
    if (!target?.id) return;
    setNotice(null);
    setWelcomeEmailError(null);
    try {
      const result = await sendWelcomeMutation.mutateAsync(target.id);
      setWelcomeEmailSentUserId(target.id);
      if (result?.email_kind === "credentials" && result?.initial_password) {
        setPasswordCopyNotice(null);
        setPasswordModal({
          username: target.username,
          password: result.initial_password,
          welcomeEmailSent: true,
        });
      } else {
        setNotice({ type: "success", message: `Welcome email sent to ${target.email || target.username}.` });
      }
    } catch (err) {
      const msg = err?.message || "Failed to send welcome email.";
      setWelcomeEmailError(msg);
      setNotice({ type: "error", message: msg });
    } finally {
      setIsWelcomeEmailDialogOpen(false);
      setWelcomeEmailTargetUser(null);
    }
  };

  const openWelcomeEmailDialog = (userRecord) => {
    setWelcomeEmailError(null);
    setWelcomeEmailTargetUser(userRecord);
    setIsWelcomeEmailDialogOpen(true);
  };

  const handleApplyDefaults = async () => {
    if (!selectedUser?.id) return;
    const errors = {};
    let effectiveRegularCode = defaultsForm.code;
    if (defaultsForm.type === "regular") {
      let code = (defaultsForm.code || "").trim();
      if (!code) errors.code = "Code is required.";
      else if (code.length > 64) errors.code = "Code must be at most 64 characters.";
      else {
        // Check if code collides with existing coupons linked to this user
        const existingCodes = (couponsQuery.data?.coupons || []).map((c) => (c.code || "").toLowerCase());
        if (existingCodes.includes(code.toLowerCase())) {
          // If auto-generated (not touched), try alternative code
          if (!defaultsForm.codeTouched && selectedUser.username) {
            const alt = buildAlternativeDefaultCouponCode(selectedUser.username, regularDefaultSuffix);
            if (!existingCodes.includes(alt.toLowerCase())) {
              effectiveRegularCode = alt;
            } else {
              errors.code = "Code already exists for this user. Edit the code to use a unique value.";
            }
          } else {
            errors.code = "Code already exists for this user. Use a different code.";
          }
        }
      }
    } else if (defaultsForm.type === "access") {
      const count = Number(defaultsForm.count);
      if (!Number.isInteger(count) || count < 1 || count > DEFAULT_ACCESS_MAX_COUNT) {
        errors.count = `Count must be a whole number between 1 and ${DEFAULT_ACCESS_MAX_COUNT}.`;
      }
    }
    setDefaultsErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setNotice(null);
    try {
      const result = await applyDefaultsMutation.mutateAsync({
        userId: selectedUser.id,
        default_coupon: defaultsForm.type === "regular" ? { code: effectiveRegularCode.trim() } : undefined,
        default_access: defaultsForm.type === "access" ? { count: Number(defaultsForm.count) } : undefined,
      });
      const codes = (result?.coupons || []).map((c) => c?.code).filter(Boolean);
      const warnings = (result?.sync_warnings || []).filter(Boolean);
      const shownCodes = codes.slice(0, NOTICE_CODE_PREVIEW_COUNT);
      const remaining = codes.length - shownCodes.length;
      const codeList = remaining > 0 ? `${shownCodes.join(", ")} (+${remaining} more)` : shownCodes.join(", ");
      const message =
        codes.length > 0
          ? `Added ${codes.length} coupon${codes.length === 1 ? "" : "s"} to ${selectedUser.username}: ${codeList}.`
          : `Coupon defaults applied to ${selectedUser.username}.`;
      if (warnings.length > 0) {
        setNotice({ type: "warning", message: `${message} Stripe sync warning: ${warnings.join("; ")}` });
      } else {
        setNotice({ type: "success", message });
      }
      setDefaultsForm(EMPTY_DEFAULTS_FORM);
      setDefaultsErrors({});
    } catch (err) {
      setDefaultsErrors({ form: err?.message || "Failed to apply coupon defaults." });
    }
  };

  const welcomeEmailIsPending = !!welcomeEmailTargetUser?.must_complete_onboarding;

  const handlePromote = async (couponId) => {
    if (!selectedUser?.id) return;
    setPromoteNotice(null);
    setPromoteLoading(true);
    try {
      await setAffiliatePromotedCoupon(selectedUser.id, couponId);
      await couponsQuery.refetch?.();
      setPromoteNotice({ type: "success", message: "Promoted coupon updated." });
    } catch (err) {
      setPromoteNotice({ type: "error", message: err?.message || "Failed to update promoted coupon." });
    } finally {
      setPromoteLoading(false);
    }
  };

  const noticeStyles = {
    success: "bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300",
    error: "bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300",
    warning:
      "bg-amber-100 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300",
  };

  const entityLabel = forceOnlyAffiliates ? "affiliates" : "users";
  const tableAriaLabel = forceOnlyAffiliates ? "Affiliates table" : "Users table";

  return (
    <>
        {affiliateAddedUser && (
          <div
            role="status"
            aria-live="polite"
            className="rounded-xl border border-emerald-500/30 bg-emerald-600 dark:bg-emerald-600 p-4 sm:p-5 text-white shadow-sm"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-3 min-w-0">
                <div className="shrink-0 rounded-full bg-white/15 p-2">
                  <CheckIcon size="xl" color="white" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <div className="font-heading text-base sm:text-lg font-bold leading-snug">
                    {affiliateAddedUser.username} has been added as {kindArticleLabel(affiliateAddedUser.kind)}
                  </div>
                  <div className="mt-1 text-sm sm:text-base text-white/90">
                    {affiliateAddedUser.kind === ACCOUNT_KIND_PARTNER
                      ? "They can now access the portal (Coupons + Analytics) and hand out the coupon packs they buy or receive."
                      : "They can now access the Affiliate dashboard (Coupons + Analytics) and earn a share of sales from referrals."}
                  </div>

                  {welcomeEmailSentUserId === affiliateAddedUser.id && (
                    <div className="mt-2 text-sm sm:text-base text-white/95">
                      Welcome email sent to{" "}
                      <span className="font-semibold break-all">{affiliateAddedUser.email}</span>.
                    </div>
                  )}

                  {welcomeEmailError && (
                    <div className="mt-2 text-sm sm:text-base text-white">
                      <span className="font-semibold">Email not sent:</span> {welcomeEmailError}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center w-full sm:w-auto">
                <SecondaryButton
                  variant="alwaysLight"
                  size="sm"
                  loading={sendWelcomeMutation.isPending}
                  disabled={
                    welcomeEmailSentUserId === affiliateAddedUser.id || !hasRealEmail(affiliateAddedUser.email)
                  }
                  title={
                    hasRealEmail(affiliateAddedUser.email)
                      ? undefined
                      : "This account has no real email address yet."
                  }
                  onClick={() => openWelcomeEmailDialog(affiliateAddedUser)}
                  className="w-full sm:w-auto whitespace-nowrap"
                >
                  {welcomeEmailSentUserId === affiliateAddedUser.id ? "Email sent" : "Send welcome email"}
                </SecondaryButton>

                <button
                  type="button"
                  onClick={() => {
                    setAffiliateAddedUser(null);
                    setIsWelcomeEmailDialogOpen(false);
                    setWelcomeEmailTargetUser(null);
                    setWelcomeEmailError(null);
                    setWelcomeEmailSentUserId(null);
                  }}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-2xl border border-white/25 bg-white/10 px-3 py-2 text-sm font-medium text-white hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/40 whitespace-nowrap"
                >
                  <XIcon size="sm" color="white" aria-hidden="true" />
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        <Card variant="glass">
          <CardHeader
            title={forceOnlyAffiliates ? "All affiliates and partners" : "User Search"}
            subtitle={
              forceOnlyAffiliates
                ? "Search affiliates and partners; open a row to change its kind, manage portal access, and review coupons."
                : "Open a row to change its account kind, manage Affiliate Portal access, and review coupons."
            }
          />
          <CardBody className="space-y-4">
            <div className="flex flex-col md:flex-row gap-3 md:items-end">
              <div className="flex-1">
                <FormField label="Search" htmlFor="affiliate-user-search" optional>
                  <SearchInput
                    id="affiliate-user-search"
                    value={qInput}
                    onChange={(e) => {
                      const value = e.target.value;
                      setQInput(value);
                      debouncedSetQ(value);
                    }}
                    placeholder="Search username or email…"
                  />
                </FormField>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex flex-wrap items-center gap-4" role="group" aria-label="Filter by kind">
                  <span className={`${typography.label} ${textColors.secondary}`}>Kind</span>
                  {kindOptions.map((option) => (
                    <Checkbox
                      key={option.value}
                      id={`user-kind-${option.value}`}
                      checked={selectedKinds.includes(option.value)}
                      onChange={() => toggleKind(option.value)}
                      label={option.label}
                    />
                  ))}
                </div>
                <SecondaryButton
                  type="button"
                  onClick={() => {
                    setQInput("");
                    setQ("");
                    setSelectedKinds([]);
                  }}
                >
                  Clear
                </SecondaryButton>
              </div>
            </div>

            {notice && (
              <div className={`rounded-2xl px-4 py-3 ${noticeStyles[notice.type]}`}>
                <p className={`${typography.bodySmall} whitespace-pre-wrap`}>{notice.message}</p>
              </div>
            )}

            <div className={`border rounded-2xl overflow-hidden ${borders.glass}`}>
              <div className={`overflow-x-auto ${surfaces.glassCard}`}>
                <table className="min-w-full text-left" aria-label={tableAriaLabel}>
                  <thead className={`${surfaces.glass}`}>
                    <tr className={`${textColors.secondary} ${typography.caption}`}>
                      <th className="px-3 py-2">Username</th>
                      <th className="px-3 py-2">Email</th>
                      <th className="px-3 py-2">Linked to</th>
                      <th className="px-3 py-2 text-right">Linked</th>
                      <th className="px-3 py-2 text-right">Published/Containers</th>
                      <th className="px-3 py-2 text-right" aria-label="Open user" />
                    </tr>
                  </thead>
                  <tbody className={`${surfaces.glassCard}`}>
                    {usersQuery.isLoading && (
                      <tr>
                        <td colSpan={6} className={`px-3 py-6 ${textColors.secondary} ${typography.bodySmall}`}>
                          Loading {entityLabel}…
                        </td>
                      </tr>
                    )}

                    {usersQuery.error && (
                      <tr>
                        <td colSpan={6} className={`px-3 py-6 ${textColors.secondary} ${typography.bodySmall}`}>
                          Failed to load {entityLabel}: {usersQuery.error?.message || "Unknown error"}
                        </td>
                      </tr>
                    )}

                    {!usersQuery.isLoading && !usersQuery.error && users.length === 0 && (
                      <tr>
                        <td colSpan={6} className={`px-3 py-6 ${textColors.secondary} ${typography.bodySmall}`}>
                          No {entityLabel} found.
                        </td>
                      </tr>
                    )}

                    {users.map((u) => (
                      <React.Fragment key={u.id}>
                        {(() => {
                          const isSelected = String(selectedUser?.id) === String(u.id);
                          const accountKind = getAccountKind(u);
                          return (
                            <tr
                              className={[
                                borders.glass,
                                "border-t",
                                "cursor-pointer",
                                isSelected ? `${surfaces.active} ${borders.glass} border` : surfaces.hover,
                              ].join(" ")}
                              onClick={() => setSelectedUser((prev) => (String(prev?.id) === String(u.id) ? null : u))}
                              aria-expanded={isSelected}
                            >
                              <td className={`px-3 ${isSelected ? "py-4" : "py-2"} ${typography.bodySmall} ${textColors.primary}`}>
                                <span className="inline-flex items-center gap-2 flex-wrap">
                                  {u.username}
                                  <AccountKindBadge kind={accountKind} />
                                  {isPortalKind(accountKind) && !!u.affiliate_portal_blocked && (
                                    <span
                                      className={`inline-flex items-center px-2 py-0.5 rounded-full ${typography.caption} bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300`}
                                    >
                                      Portal blocked
                                    </span>
                                  )}
                                  {!!u.must_complete_onboarding && (
                                    <span
                                      className={`inline-flex items-center px-2 py-0.5 rounded-full ${typography.caption} bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300`}
                                    >
                                      Pending first access
                                    </span>
                                  )}
                                </span>
                              </td>
                              <td className={`px-3 ${isSelected ? "py-4" : "py-2"} ${typography.bodySmall} ${textColors.secondary}`}>
                                {u.email}
                              </td>
                              {/* Who this account was signed up under; blank when nobody. */}
                              <td className={`px-3 ${isSelected ? "py-4" : "py-2"} ${typography.bodySmall} ${textColors.secondary}`}>
                                {u.linked_to_username || ""}
                              </td>
                              {/* Accounts linked to this one — only portal kinds carry any. */}
                              <td className={`px-3 ${isSelected ? "py-4" : "py-2"} ${typography.bodySmall} ${textColors.secondary} text-right`}>
                                {isPortalKind(accountKind) ? u.linked_user_count ?? 0 : ""}
                              </td>
                              <td className={`px-3 ${isSelected ? "py-4" : "py-2"} ${typography.bodySmall} ${textColors.secondary} text-right`}>
                                {`${u.published_container_count ?? 0}/${u.container_count ?? 0}`}
                              </td>
                              <td className={`px-3 ${isSelected ? "py-4" : "py-2"} text-right`} onClick={(e) => e.stopPropagation()}>
                                <button
                                  type="button"
                                  className={`inline-flex items-center justify-center rounded-2xl px-2 py-1 ${surfaces.hover} ${textColors.primary}`}
                                  aria-label={`Open ${u.username}`}
                                  onClick={() => navigate(`/admin/dashboard/users/${u.id}`)}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"
                                    />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7a4 4 0 108 0 4 4 0 00-8 0z" />
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          );
                        })()}

                        {String(selectedUser?.id) === String(u.id) && (() => {
                          const accountKind = getAccountKind(u);
                          const isPortalAccount = isPortalKind(accountKind);
                          return (
                            <tr className={`${borders.glass} border-t`}>
                              <td colSpan={6} className={`px-3 pb-4 ${surfaces.glassCard}`}>
                                {/* Account section — every action that changes this
                                    account lives here, never on the collapsed row. */}
                                <div className={`mt-3 rounded-2xl border ${borders.glass} ${surfaces.glass} p-3`}>
                                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                    <div>
                                      <div className={`${typography.label} ${textColors.primary}`}>Account</div>
                                      <div className={`mt-1 flex items-center gap-2 ${typography.caption} ${textColors.muted}`}>
                                        <span>Kind</span>
                                        <AccountKindBadge kind={accountKind} />
                                      </div>
                                    </div>

                                    <div
                                      className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto sm:justify-end flex-wrap"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <SecondaryButton
                                        type="button"
                                        size="sm"
                                        disabled={setCommercialKindMutation.isPending}
                                        aria-label={`Change kind for ${u.username}`}
                                        onClick={() => openKindPicker(u)}
                                        className="w-full sm:w-auto whitespace-nowrap"
                                      >
                                        Change kind
                                      </SecondaryButton>

                                      {!!u.must_complete_onboarding && (
                                        <SecondaryButton
                                          type="button"
                                          size="sm"
                                          disabled={regeneratePasswordMutation.isPending}
                                          aria-label={`Regenerate password for ${u.username}`}
                                          onClick={() => setRegenerateTarget(u)}
                                          className="w-full sm:w-auto whitespace-nowrap"
                                        >
                                          Regenerate password
                                        </SecondaryButton>
                                      )}

                                      {isPortalAccount && (
                                        <SecondaryButton
                                          type="button"
                                          size="sm"
                                          aria-label={`Pack pricing for ${u.username}`}
                                          onClick={() => {
                                            setPricingNotice(null);
                                            setIsRemoveOverrideConfirmOpen(false);
                                            setPricingUser(u);
                                          }}
                                          className="w-full sm:w-auto whitespace-nowrap"
                                        >
                                          Pack pricing
                                        </SecondaryButton>
                                      )}

                                      {hasAffiliatePortalAccess(u) && (
                                        <SecondaryButton
                                          type="button"
                                          size="sm"
                                          loading={sendWelcomeMutation.isPending}
                                          disabled={
                                            sendWelcomeMutation.isPending ||
                                            welcomeEmailSentUserId === u.id ||
                                            !hasRealEmail(u.email)
                                          }
                                          title={
                                            hasRealEmail(u.email)
                                              ? u.must_complete_onboarding
                                                ? "Emails username, temporary password, and active coupon links."
                                                : "Emails affiliate program details and active coupon links."
                                              : "This account has no real email address yet."
                                          }
                                          onClick={() => openWelcomeEmailDialog(u)}
                                          className="w-full sm:w-auto whitespace-nowrap"
                                        >
                                          {welcomeEmailSentUserId === u.id ? "Email sent" : "Send welcome email"}
                                        </SecondaryButton>
                                      )}

                                      {/* Last action on the row: shutting the portal is the
                                          one destructive thing here, and it reads as its own
                                          undo once it has been done. */}
                                      {isPortalAccount &&
                                        (hasAffiliatePortalAccess(u) ? (
                                          <DangerButton
                                            type="button"
                                            size="sm"
                                            disabled={setFlagMutation.isPending}
                                            title="Keeps the role and coupon links; it only shuts the portal."
                                            aria-label={`Block portal access for ${u.username}`}
                                            onClick={() => requestAffiliateToggle(u, false)}
                                            className="w-full sm:w-auto whitespace-nowrap"
                                          >
                                            Block portal access
                                          </DangerButton>
                                        ) : (
                                          <SecondaryButton
                                            type="button"
                                            size="sm"
                                            disabled={setFlagMutation.isPending}
                                            aria-label={`Unblock portal access for ${u.username}`}
                                            onClick={() => requestAffiliateToggle(u, true)}
                                            className="w-full sm:w-auto whitespace-nowrap"
                                          >
                                            Unblock portal access
                                          </SecondaryButton>
                                        ))}
                                    </div>
                                  </div>
                                </div>

                                {/* Websites section */}
                                <div className={`mt-3 rounded-2xl border ${borders.glass} ${surfaces.glass} p-3`}>
                                  <div>
                                    <div className={`${typography.label} ${textColors.primary}`}>Websites by {u.username}</div>
                                    <div className={`${typography.caption} ${textColors.muted}`}>
                                      Each website container and a link to open the live site.
                                    </div>
                                  </div>

                                  <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                                    {userDetailQuery.isLoading ? (
                                      <div className={`${typography.bodySmall} ${textColors.secondary}`}>Loading websites…</div>
                                    ) : userDetailQuery.error ? (
                                      <div className={`${typography.bodySmall} ${textColors.secondary}`}>
                                        Failed to load websites: {userDetailQuery.error?.message || "Unknown error"}
                                      </div>
                                    ) : userContainers.length === 0 ? (
                                      <div className={`${typography.bodySmall} ${textColors.secondary}`}>No websites yet.</div>
                                    ) : (
                                      <div className="space-y-2">
                                        {userContainers.map((c) => {
                                          const isContainerExpanded = String(expandedContainerId) === String(c.id);
                                          return (
                                            <div
                                              key={c.id}
                                              className={`rounded-2xl ${surfaces.glassCard} ${borders.glass} border`}
                                            >
                                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 py-2">
                                                <div className="min-w-0">
                                                  <div className={`${typography.bodySmall} ${textColors.primary} font-medium truncate`}>
                                                    {c.name || "Untitled website"}
                                                  </div>
                                                  {c.domain ? (
                                                    <a
                                                      href={`https://${c.domain}`}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      className={`${interactive.link} ${interactive.linkHover} ${typography.caption} block truncate`}
                                                      title={c.domain}
                                                    >
                                                      {c.domain}
                                                    </a>
                                                  ) : (
                                                    <span className={`${typography.caption} ${textColors.muted}`}>No domain set</span>
                                                  )}
                                                </div>
                                                <div className="flex items-center gap-2 shrink-0">
                                                  <span className={`px-2 py-0.5 rounded-full ${typography.caption} ${surfaces.glass} ${borders.glass} border ${textColors.secondary}`}>
                                                    {c.website_count ?? 0} version{(c.website_count ?? 0) === 1 ? "" : "s"}
                                                  </span>
                                                  <span
                                                    className={`px-2 py-0.5 rounded-full ${typography.caption} ${
                                                      (c.published_count ?? 0) > 0
                                                        ? "bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300"
                                                        : `${surfaces.glass} ${borders.glass} border ${textColors.secondary}`
                                                    }`}
                                                  >
                                                    {c.published_count ?? 0} published
                                                  </span>
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      setExpandedContainerId((prev) =>
                                                        String(prev) === String(c.id) ? null : c.id,
                                                      )
                                                    }
                                                    aria-expanded={isContainerExpanded}
                                                    className={`inline-flex items-center gap-1 rounded-2xl px-3 py-1 ${typography.button} ${surfaces.hover} ${textColors.primary} ${borders.glass} border whitespace-nowrap`}
                                                  >
                                                    {isContainerExpanded ? "Hide versions" : "Versions"}
                                                    <svg
                                                      className={`w-3.5 h-3.5 transition-transform ${isContainerExpanded ? "rotate-180" : ""}`}
                                                      fill="none"
                                                      stroke="currentColor"
                                                      viewBox="0 0 24 24"
                                                      aria-hidden="true"
                                                    >
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                  </button>
                                                  {c.domain && (
                                                    <a
                                                      href={`https://${c.domain}`}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      className={`inline-flex items-center gap-1 rounded-2xl px-3 py-1 ${typography.button} ${surfaces.hover} ${textColors.primary} ${borders.glass} border whitespace-nowrap`}
                                                    >
                                                      Open
                                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                      </svg>
                                                    </a>
                                                  )}
                                                </div>
                                              </div>

                                              {isContainerExpanded && (
                                                <div className={`border-t ${borders.glass} px-3 py-2`}>
                                                  {containerVersionsQuery.isLoading ? (
                                                    <div className={`${typography.caption} ${textColors.secondary}`}>Loading versions…</div>
                                                  ) : containerVersionsQuery.error ? (
                                                    <div className={`${typography.caption} ${textColors.secondary}`}>
                                                      Failed to load versions: {containerVersionsQuery.error?.message || "Unknown error"}
                                                    </div>
                                                  ) : (containerVersionsQuery.data?.versions || []).length === 0 ? (
                                                    <div className={`${typography.caption} ${textColors.secondary}`}>No versions found.</div>
                                                  ) : (
                                                    <div className="space-y-1.5">
                                                      {(containerVersionsQuery.data?.versions || []).map((v) => (
                                                        <div
                                                          key={v.id}
                                                          className={`flex items-center justify-between gap-2 rounded-xl ${surfaces.glass} ${borders.glass} border px-3 py-1.5`}
                                                        >
                                                          <div className="min-w-0">
                                                            <div className={`${typography.bodySmall} ${textColors.primary} truncate`}>
                                                              {v.version_name || "Untitled version"}
                                                            </div>
                                                            <div className={`${typography.caption} ${textColors.muted}`}>
                                                              Edited {formatIsoDateTime(v.last_edited_at)}
                                                            </div>
                                                          </div>
                                                          <div className="flex items-center gap-2 shrink-0">
                                                            <span
                                                              className={`px-2 py-0.5 rounded-full ${typography.caption} ${
                                                                v.is_published
                                                                  ? "bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300"
                                                                  : `${surfaces.glass} ${borders.glass} border ${textColors.secondary}`
                                                              }`}
                                                            >
                                                              {v.is_published ? "Published" : "Draft"}
                                                            </span>
                                                            {c.domain ? (
                                                              <a
                                                                href={`/view/${u.username}/${c.domain}/${v.version_slug}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className={`inline-flex items-center gap-1 rounded-2xl px-3 py-1 ${typography.button} bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 text-white whitespace-nowrap`}
                                                              >
                                                                View
                                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                                </svg>
                                                              </a>
                                                            ) : (
                                                              <span className={`${typography.caption} ${textColors.muted}`}>No domain</span>
                                                            )}
                                                          </div>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Coupons only exist for portal accounts, so a plain
                                    user's row stops at their websites. */}
                                {isPortalAccount && (
                                  <>
                                    {/* Coupons section — regular and access coupons read
                                        very differently, so each gets its own table. */}
                                    <div className={`mt-3 rounded-2xl border ${borders.glass} ${surfaces.glass} p-3`}>
                                      <div>
                                        <div className={`${typography.label} ${textColors.primary}`}>Coupons linked to {u.username}</div>
                                        <div className={`${typography.caption} ${textColors.muted}`}>
                                          Access coupons show the same send/redemption status the affiliate or partner sees in their own portal.
                                        </div>
                                      </div>

                                      <div className="mt-3 space-y-4" onClick={(e) => e.stopPropagation()}>
                                        {couponsQuery.isLoading ? (
                                          <div className={`${typography.bodySmall} ${textColors.secondary}`}>Loading coupons…</div>
                                        ) : couponsQuery.error ? (
                                          <div className={`${typography.bodySmall} ${textColors.secondary}`}>
                                            Failed to load coupons: {couponsQuery.error?.message || "Unknown error"}
                                          </div>
                                        ) : linkedCoupons.length === 0 ? (
                                          <div className={`${typography.bodySmall} ${textColors.secondary}`}>No coupons linked.</div>
                                        ) : (
                                          <>
                                            <LinkedCouponsTable
                                              title="Coupons"
                                              emptyMessage="No regular coupons linked."
                                              coupons={regularLinkedCoupons}
                                              promotedCouponId={promotedCouponId}
                                              promoteLoading={promoteLoading}
                                              onPromote={handlePromote}
                                            />
                                            <LinkedCouponsTable
                                              title="Access coupons"
                                              emptyMessage="No access coupons linked."
                                              coupons={accessLinkedCoupons}
                                              isAccess
                                              openRecipientsCouponId={openRecipientsCouponId}
                                              onToggleRecipients={(couponId) =>
                                                setOpenRecipientsCouponId((prev) =>
                                                  String(prev) === String(couponId) ? null : couponId,
                                                )
                                              }
                                            />
                                          </>
                                        )}

                                        {promoteNotice && (
                                          <div className={`mt-3 rounded-2xl px-4 py-3 ${noticeStyles[promoteNotice.type]}`}>
                                            <p className={`${typography.bodySmall} whitespace-pre-wrap`}>{promoteNotice.message}</p>
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {/* Add default coupon — inline, directly under this
                                        affiliate's coupons, so it always targets this user. */}
                                    <div className={`mt-3 rounded-2xl border ${borders.glass} ${surfaces.glass} p-3`}>
                                      <div>
                                        <div className={`${typography.label} ${textColors.primary}`}>
                                          Add coupon to {u.username}
                                        </div>
                                        <div className={`${typography.caption} ${textColors.muted}`}>
                                          Created from the global default templates and linked to {u.username} automatically.
                                        </div>
                                      </div>

                                      <div className="mt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                                        {regularDefaultQuery.isLoading || accessDefaultQuery.isLoading ? (
                                          <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                                            Loading default templates…
                                          </p>
                                        ) : !defaultsForm.type ? (
                                          <div className="space-y-2">
                                            <div className="flex flex-wrap items-center gap-2">
                                              <SecondaryButton
                                                type="button"
                                                size="sm"
                                                disabled={!regularDefaultConfigured || applyDefaultsMutation.isPending}
                                                onClick={() => {
                                                  setDefaultsForm((f) => ({ ...f, type: "regular" }));
                                                  setDefaultsErrors({});
                                                }}
                                              >
                                                Add default coupon
                                              </SecondaryButton>
                                              <SecondaryButton
                                                type="button"
                                                size="sm"
                                                disabled={!accessDefaultConfigured || applyDefaultsMutation.isPending}
                                                onClick={() => {
                                                  setDefaultsForm((f) => ({ ...f, type: "access" }));
                                                  setDefaultsErrors({});
                                                }}
                                              >
                                                Add default access coupons
                                              </SecondaryButton>
                                            </div>
                                            {!regularDefaultConfigured && (
                                              <p className={`${typography.caption} ${textColors.muted}`}>
                                                No default coupon template is configured. Set one up on the Coupons page first.
                                              </p>
                                            )}
                                            {!accessDefaultConfigured && (
                                              <p className={`${typography.caption} ${textColors.muted}`}>
                                                No default access-coupon template is configured. Set one up on the Coupons page first.
                                              </p>
                                            )}
                                          </div>
                                        ) : (
                                          <>
                                            {defaultsForm.type === "regular" && (
                                              <div className={`rounded-2xl border ${borders.glass} ${surfaces.glassCard} p-3 space-y-2`}>
                                                <div className={`${typography.bodySmall} ${textColors.primary} font-medium`}>
                                                  Default coupon
                                                </div>
                                                <FormField label="Code" htmlFor={`apply-default-coupon-code-${u.id}`}>
                                                  <Input
                                                    id={`apply-default-coupon-code-${u.id}`}
                                                    value={defaultsForm.code}
                                                    maxLength={64}
                                                    onChange={(e) => {
                                                      const value = e.target.value.slice(0, 64);
                                                      setDefaultsForm((f) => ({ ...f, code: value, codeTouched: true }));
                                                      setDefaultsErrors((p) => ({ ...p, code: undefined }));
                                                    }}
                                                    error={defaultsErrors.code}
                                                    disabled={applyDefaultsMutation.isPending}
                                                  />
                                                </FormField>
                                              </div>
                                            )}

                                            {defaultsForm.type === "access" && (
                                              <div className={`rounded-2xl border ${borders.glass} ${surfaces.glassCard} p-3 space-y-2`}>
                                                <div className={`${typography.bodySmall} ${textColors.primary} font-medium`}>
                                                  Default access coupons
                                                </div>
                                                <FormField
                                                  label="Count"
                                                  htmlFor={`apply-default-access-count-${u.id}`}
                                                  helperText={`1–${DEFAULT_ACCESS_MAX_COUNT} coupons.`}
                                                >
                                                  <Input
                                                    id={`apply-default-access-count-${u.id}`}
                                                    type="number"
                                                    min="1"
                                                    max={DEFAULT_ACCESS_MAX_COUNT}
                                                    value={defaultsForm.count}
                                                    onChange={(e) => {
                                                      const value = e.target.value;
                                                      setDefaultsForm((f) => ({ ...f, count: value, countTouched: true }));
                                                      setDefaultsErrors((p) => ({ ...p, count: undefined }));
                                                    }}
                                                    error={defaultsErrors.count}
                                                    disabled={applyDefaultsMutation.isPending}
                                                  />
                                                </FormField>
                                              </div>
                                            )}

                                            {defaultsErrors.form && (
                                              <div className={`rounded-2xl px-4 py-3 ${noticeStyles.error}`}>
                                                <p className={`${typography.bodySmall} whitespace-pre-wrap`}>
                                                  {defaultsErrors.form}
                                                </p>
                                              </div>
                                            )}

                                            <div className="flex items-center justify-end gap-2">
                                              <SecondaryButton
                                                type="button"
                                                size="sm"
                                                disabled={applyDefaultsMutation.isPending}
                                                onClick={() => {
                                                  setDefaultsForm(EMPTY_DEFAULTS_FORM);
                                                  setDefaultsErrors({});
                                                }}
                                              >
                                                Cancel
                                              </SecondaryButton>
                                              <PrimaryButton
                                                type="button"
                                                size="sm"
                                                onClick={handleApplyDefaults}
                                                loading={applyDefaultsMutation.isPending}
                                              >
                                                Add coupon
                                              </PrimaryButton>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })()}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <p className={`${typography.caption} ${textColors.muted}`}>
              Showing up to {DEFAULT_LIMIT} {entityLabel}.
            </p>
          </CardBody>
        </Card>

        <ConfirmDialog
          isOpen={!!affiliateToggleConfirm}
          onClose={() => {
            if (setFlagMutation.isPending) return;
            setAffiliateToggleConfirm(null);
          }}
          onConfirm={handleConfirmAffiliateToggle}
          variant={affiliateToggleConfirm?.nextValue ? "warning" : "danger"}
          title={
            affiliateToggleConfirm?.nextValue
              ? "Unblock Affiliate Portal access?"
              : "Block Affiliate Portal access?"
          }
          message={
            affiliateToggleConfirm?.nextValue
              ? `Confirm restoring Affiliate Portal access for ${affiliateToggleConfirm?.user?.username || "this user"}. They will be able to sign in to the Affiliate Portal again. This should only be done intentionally.`
              : `Confirm blocking Affiliate Portal access for ${affiliateToggleConfirm?.user?.username || "this user"}. Their affiliate role and coupon links stay in place, but they will not be able to use the Affiliate Portal until you unblock them. This action requires confirmation so it is harder to undo by accident.`
          }
          confirmText={affiliateToggleConfirm?.nextValue ? "Unblock portal access" : "Block portal access"}
          cancelText="Cancel"
          isLoading={setFlagMutation.isPending}
          size="md"
        />

        <ConfirmDialog
          isOpen={isWelcomeEmailDialogOpen}
          onClose={() => {
            if (sendWelcomeMutation.isPending) return;
            setIsWelcomeEmailDialogOpen(false);
            setWelcomeEmailTargetUser(null);
          }}
          onConfirm={handleSendWelcomeEmail}
          variant={welcomeEmailIsPending ? "info" : "success"}
          title="Send welcome email?"
          message={
            welcomeEmailIsPending
              ? welcomeEmailTargetUser?.email
                ? `This emails ${welcomeEmailTargetUser.username} their username and a new temporary password at ${welcomeEmailTargetUser.email}, plus any active coupon links. Because passwords are only stored hashed, a new one is generated — any password you shared before will stop working.`
                : "This emails the affiliate their username and a new temporary password, plus any active coupon links."
              : welcomeEmailTargetUser?.email
                ? `This will send a welcome email to ${welcomeEmailTargetUser.email} explaining what an affiliate is, how to access the Affiliate dashboard, and how earnings work. Their active coupon links are included when they have any.`
                : "This will send a welcome email explaining what an affiliate is, how to access the Affiliate dashboard, and how earnings work. Their active coupon links are included when they have any."
          }
          confirmText="Send email"
          cancelText="Cancel"
          isLoading={sendWelcomeMutation.isPending}
          size="md"
        />

        {/* Kind picker: choose the target kind and confirm in one place, so the
            admin reads what the switch means before committing to it. */}
        <Modal
          isOpen={!!kindChangeTarget}
          onClose={closeKindPicker}
          title={kindChangeTarget ? `Change kind for ${kindChangeTarget.user?.username}` : "Change kind"}
          size="md"
          footer={
            <div className="flex items-center justify-end gap-2">
              <SecondaryButton type="button" onClick={closeKindPicker} disabled={setCommercialKindMutation.isPending}>
                Cancel
              </SecondaryButton>
              <PrimaryButton
                type="button"
                onClick={handleConfirmKindChange}
                disabled={!kindChangeTarget?.nextKind}
                loading={setCommercialKindMutation.isPending}
              >
                {kindChangeTarget?.nextKind
                  ? `Make ${kindLabel(kindChangeTarget.nextKind).toLowerCase()}`
                  : "Change kind"}
              </PrimaryButton>
            </div>
          }
        >
          {kindChangeTarget && (
            <div className="space-y-4">
              <div className={`flex items-center gap-2 ${typography.bodySmall} ${textColors.secondary}`}>
                <span>Currently</span>
                <AccountKindBadge kind={getAccountKind(kindChangeTarget.user)} />
              </div>

              <div role="radiogroup" aria-label="New account kind" className="space-y-2">
                {ACCOUNT_KINDS.filter((kind) => kind !== getAccountKind(kindChangeTarget.user)).map((kind) => {
                  const isSelected = kindChangeTarget.nextKind === kind;
                  return (
                    <button
                      key={kind}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      disabled={setCommercialKindMutation.isPending}
                      onClick={() => setKindChangeTarget((prev) => ({ ...prev, nextKind: kind }))}
                      className={`w-full text-left p-3 border rounded-2xl transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed ${
                        isSelected
                          ? "border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-200 dark:ring-blue-900/50"
                          : `${borders.glass} ${surfaces.glass} ${surfaces.hover}`
                      }`}
                    >
                      <span className={`block ${typography.bodySmall} font-medium ${textColors.primary}`}>
                        {`Make ${kindLabel(kind).toLowerCase()}`}
                      </span>
                      <span className={`block mt-0.5 ${typography.caption} ${textColors.secondary}`}>
                        {`${kindChangeTarget.user?.username} ${ACCOUNT_KIND_CONSEQUENCES[kind]}`}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </Modal>

        <ConfirmDialog
          isOpen={!!regenerateTarget}
          onClose={() => {
            if (regeneratePasswordMutation.isPending) return;
            setRegenerateTarget(null);
          }}
          onConfirm={handleRegeneratePassword}
          title="Regenerate password?"
          message={
            regenerateTarget
              ? `This will replace the current initial password for ${regenerateTarget.username}. The old password will stop working immediately.`
              : "This will replace the current initial password."
          }
          variant="info"
          confirmText="Regenerate"
          cancelText="Cancel"
          isLoading={regeneratePasswordMutation.isPending}
        />

        <Modal
          isOpen={!!pricingUser && !isRemoveOverrideConfirmOpen}
          onClose={() => {
            if (saveUserPricingMutation.isPending || deleteUserPricingMutation.isPending) return;
            closePricingModal();
          }}
          title={pricingUser ? `Pack pricing for ${pricingUser.username}` : "Pack pricing"}
          size="lg"
        >
          {pricingUser && (
            <div className="space-y-4">
              {userPricingQuery.isLoading ? (
                <p className={`${typography.bodySmall} ${textColors.secondary}`}>Loading pack pricing…</p>
              ) : userPricingQuery.error ? (
                <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                  Failed to load pack pricing: {userPricingQuery.error?.message || "Unknown error"}
                </p>
              ) : (
                <>
                  <div>
                    <p className={`${typography.bodySmall} ${textColors.primary}`}>
                      {userPricingQuery.data?.has_override
                        ? `${pricingUser.username} has a custom pricing table that fully replaces the default.`
                        : `${pricingUser.username} uses the default pricing table. Saving creates a custom table for them.`}
                    </p>
                    <p className={`mt-1 ${typography.caption} ${textColors.muted}`}>
                      Volume-discount tiers for coupon-pack orders; the tier with the highest minimum quantity at or
                      below the order size applies.
                    </p>
                  </div>

                  {!!userPricingQuery.data?.override_user_rules && (
                    <div className={`rounded-2xl px-4 py-3 ${noticeStyles.warning}`}>
                      <p className={typography.bodySmall}>
                        The global default currently overrides all user-specific pricing.
                      </p>
                    </div>
                  )}

                  <CouponPackPricingEditor
                    tiers={userPricingQuery.data?.tiers || []}
                    saving={saveUserPricingMutation.isPending}
                    disabled={deleteUserPricingMutation.isPending}
                    saveLabel="Save override"
                    idPrefix={`user-pack-pricing-${pricingUser.id}`}
                    onSave={handleSaveUserPricing}
                    onCancel={closePricingModal}
                  />

                  {!!userPricingQuery.data?.has_override && (
                    <div>
                      <DangerButton
                        type="button"
                        size="sm"
                        disabled={saveUserPricingMutation.isPending || deleteUserPricingMutation.isPending}
                        onClick={() => setIsRemoveOverrideConfirmOpen(true)}
                      >
                        Remove override
                      </DangerButton>
                    </div>
                  )}

                  {pricingNotice && (
                    <div className={`rounded-2xl px-4 py-3 ${noticeStyles[pricingNotice.type]}`}>
                      <p className={`${typography.bodySmall} whitespace-pre-wrap`}>{pricingNotice.message}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </Modal>

        <ConfirmDialog
          isOpen={isRemoveOverrideConfirmOpen}
          onClose={() => {
            if (deleteUserPricingMutation.isPending) return;
            setIsRemoveOverrideConfirmOpen(false);
          }}
          onConfirm={handleRemoveUserPricing}
          variant="danger"
          title="Remove pricing override?"
          message={`${pricingUser?.username || "This user"} will go back to the default pack pricing table.`}
          confirmText="Remove override"
          cancelText="Cancel"
          isLoading={deleteUserPricingMutation.isPending}
          size="md"
        />

        <Modal
          isOpen={!!passwordModal}
          onClose={() => {
            setPasswordModal(null);
            setPasswordCopyNotice(null);
          }}
          title="New password generated"
          size="md"
          footer={
            <SecondaryButton
              type="button"
              onClick={() => {
                setPasswordModal(null);
                setPasswordCopyNotice(null);
              }}
            >
              Done
            </SecondaryButton>
          }
        >
          {passwordModal && (
            <div className="space-y-4">
              {passwordModal.welcomeEmailError ? (
                <div className={`rounded-2xl px-4 py-3 ${noticeStyles.warning}`}>
                  <p className={`${typography.bodySmall} whitespace-pre-wrap`}>
                    The welcome email could not be sent ({passwordModal.welcomeEmailError}). Share the password below manually.
                  </p>
                </div>
              ) : passwordModal.welcomeEmailSent ? (
                <div className={`rounded-2xl px-4 py-3 ${noticeStyles.success}`}>
                  <p className={typography.bodySmall}>
                    A welcome email with these credentials was sent to the affiliate.
                  </p>
                </div>
              ) : null}

              <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                Initial password for <span className={`${textColors.primary} font-medium`}>{passwordModal.username}</span>:
              </p>

              <div className="flex items-center gap-2 min-w-0">
                <code
                  className={`flex-1 min-w-0 px-3 py-2 rounded-2xl ${surfaces.glass} ${borders.glass} border ${typography.bodySmall} ${textColors.primary} break-all`}
                  aria-label="Initial password"
                >
                  {passwordModal.password}
                </code>
                <IconButton
                  size="sm"
                  variant="ghost"
                  aria-label="Copy initial password"
                  title="Copy"
                  icon={<CopyIcon size="sm" />}
                  onClick={async () => {
                    setPasswordCopyNotice(null);
                    try {
                      if (navigator?.clipboard?.writeText) {
                        await navigator.clipboard.writeText(passwordModal.password);
                        setPasswordCopyNotice({ type: "success", message: "Copied password." });
                      } else {
                        throw new Error("Clipboard not available");
                      }
                    } catch (_e) {
                      setPasswordCopyNotice({
                        type: "error",
                        message: "Could not copy automatically. Please copy the password manually.",
                      });
                    }
                  }}
                />
              </div>

              {passwordCopyNotice && (
                <div className={`rounded-2xl px-3 py-2 ${noticeStyles[passwordCopyNotice.type]}`}>
                  <p className={`${typography.bodySmall}`}>{passwordCopyNotice.message}</p>
                </div>
              )}

              <div className="rounded-2xl px-4 py-3 bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300">
                <p className={`${typography.bodySmall}`}>
                  This password will not be shown again. Share it with the affiliate now — if it is lost, you can only
                  regenerate a new one.
                </p>
              </div>
            </div>
          )}
        </Modal>
    </>
  );
};

export default AdminAffiliateUsersPanel;
